import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiTokenRequest, loginRequest } from './msalConfig';
import { RedirectInProgressError, isInteractionInProgress } from './redirectInProgress';
import {
  type Entitlement,
  entitlementFromError,
  entitlementFromRoles,
  isNotAssignedError,
} from './entitlement';
import {
  fetchEffectiveRoles,
  clearLastKnownEffectiveRoles,
  forgetEffectiveRoles,
  getLastKnownEffectiveRoles,
  subscribeToEffectiveRolesInvalidation,
} from './effectiveRoles';
import { needsInteraction } from './needsInteraction';
import { isAuthRedirectInFlight, signIn, signOut, startReauth } from './authSession';

export function useAuth() {
  // `inProgress` (#14687) is msal-react's view of whether an interaction is already underway.
  // Both getAccessToken and handleSessionExpired consult it so only ONE top-level navigation is
  // ever issued — the double-navigation was the verified cause of the timeout-only sign-in failure.
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;

  const login = useCallback(() => {
    // loginRequest includes the API scope (#13569) so consent + an API token are
    // acquired during the interactive sign-in — avoids a silent-acquire miss on
    // the first API call triggering a second, unwanted acquireTokenRedirect prompt.
    // #24685 — single-flight, retries once past a stuck interaction marker, never rejects.
    void signIn(instance, loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    // #21587 — a signed-out session must not leave effective roles cached for the next sign-in.
    // #24685 — sign-out is the one thing (with an account change) that drops last-known roles.
    forgetEffectiveRoles();
    void signOut(instance, account);
  }, [instance, account]);

  // Called when the API rejects the session (a 401 that a forced token refresh did not cure).
  //
  // #24685 — session expiry is not sign-out. While the account is still present the operator is
  // re-authenticated in place (single-flight, back to the same page) instead of being logged out
  // and sent to the landing page. Sign-out remains the fallback when there is no account, or when a
  // re-auth already ran moments ago and did not help (redirecting again would loop).
  const handleSessionExpired = useCallback(() => {
    // #14687 — if an auth navigation is already underway, that navigation IS the recovery.
    // The module flag is authoritative (#24685); msal-react's inProgress is kept as a second,
    // fail-open signal for interactions this module did not start (e.g. handleRedirect settling).
    if (isAuthRedirectInFlight() || isInteractionInProgress(inProgress)) return;
    if (account && startReauth(instance, account, apiTokenRequest) !== 'suppressed') return;
    forgetEffectiveRoles();
    void signOut(instance, account);
  }, [instance, account, inProgress]);

  // forceRefresh bypasses MSAL's cached silent token — used by useApi to get one
  // fresh re-acquire attempt before treating a 401 as a genuine session expiry (#13569).
  const getAccessToken = useCallback(async (forceRefresh = false): Promise<string> => {
    if (!account) throw new Error('No authenticated account');
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
        forceRefresh,
      });
      return response.accessToken;
    } catch (error) {
      // #24685 — `needsInteraction` covers InteractionRequiredAuthError AND the BrowserAuthError /
      // ServerError codes silent renewal fails with after a long idle (`timed_out` above all).
      // Those used to be rethrown: an unhandled rejection, and no re-authentication.
      if (!needsInteraction(error)) throw error;
      // #14687: throw a sentinel rather than `return ''` — an empty string was indistinguishable
      // from a real token, so the caller fetched with an empty bearer and logged the operator out
      // on top of the redirect. Callers must not treat the sentinel as a session expiry.
      if (isInteractionInProgress(inProgress)) throw new RedirectInProgressError();
      // #24685 — single-flight: however many callers fail together, exactly one redirect starts.
      // 'suppressed' means a re-auth already ran moments ago without curing this; another redirect
      // would loop, so the original failure is surfaced instead.
      if (startReauth(instance, account, apiTokenRequest) === 'suppressed') throw error;
      throw new RedirectInProgressError();
    }
  }, [instance, account, inProgress]);

  // #21587 — roles come from the API's effective-roles endpoint, NOT from the access token's
  // `roles` claim. #16666 moved the fine staff roles (Operations / Infra / App) into each
  // environment's StaffRoles table; the API adds them to the principal server-side, so the token
  // alone no longer carries every role the API authorises with. The token is still acquired here
  // first: it authenticates the request, and Entra refusing it with AADSTS50105 is still how a
  // not-assigned account is detected (BR-S-EFR-004, unchanged).
  const [roles, setRoles] = useState<string[]>([]);
  // True until the first roles resolution completes. Consumers that REDIRECT on
  // a missing role (e.g. an Owner-only page) must wait for this to be false —
  // otherwise the initial empty `roles` would bounce an authorised user away
  // before their roles load.
  const [rolesLoading, setRolesLoading] = useState(true);
  // Bug #19757 — whether this account is entitled to XCentral at all. Distinct from `roles`:
  // an empty role set used to mean "no roles", "not assigned", AND "the lookup failed", and the
  // shell rendered for all three. See ./entitlement.ts for the four states.
  const [entitlement, setEntitlement] = useState<Entitlement>('loading');
  // Bumped when the effective-roles cache is invalidated (e.g. the signed-in user saved their own
  // roles on Users & Roles), so every mounted useAuth refetches. The fetch itself is single-flight.
  const [rolesGeneration, setRolesGeneration] = useState(0);

  useEffect(
    () => subscribeToEffectiveRolesInvalidation(() => setRolesGeneration((g) => g + 1)),
    [],
  );

  // The account whose roles are currently on screen (null until the first success).
  const resolvedAccountKey = useRef<string | null>(null);

  const accountKey = account ? account.homeAccountId || account.localAccountId || account.username : '';

  useEffect(() => {
    if (!account) {
      resolvedAccountKey.current = null;
      // #24685 — no account is signed out: nothing about the previous session's roles survives.
      // (Not forgetEffectiveRoles: that notifies subscribers, which would re-run this effect.)
      clearLastKnownEffectiveRoles();
      setRoles([]);
      setRolesLoading(false);
      // No account is "signed out", which AuthGuard handles before anything reads this. It is
      // never a denial.
      setEntitlement('loading');
      return;
    }
    let cancelled = false;
    // A refetch for the SAME account (an invalidation) keeps the current roles on screen until the
    // new answer arrives, rather than dropping the whole shell back to loading. Only the first
    // resolution for an account goes through 'loading'.
    const isRefresh = resolvedAccountKey.current === accountKey;
    if (!isRefresh) {
      setRolesLoading(true);
      setEntitlement('loading');
    }

    // Bug #24685 — a failed lookup for an account whose roles have ALREADY resolved on this page
    // load (an invalidation refetch, or a component mounting after the first resolution) is a
    // background refresh, not a verdict. It used to set roles [] + transientError, which is what
    // hid role-gated navigation (the XCentral Hub sidebar item) after hours idle. The last-known
    // roles stay; only sign-out or an account change drops them. Returns false when there is no
    // earlier answer — a genuine first load — so the caller keeps the #19757 behaviour.
    const keepLastKnownRoles = (): boolean => {
      const lastKnown = getLastKnownEffectiveRoles(accountKey);
      if (!isRefresh && lastKnown === null) return false;
      if (!isRefresh && lastKnown !== null) {
        // This hook has not shown roles yet, but the account resolved elsewhere on the page.
        resolvedAccountKey.current = accountKey;
        setRoles(lastKnown);
        setEntitlement(entitlementFromRoles(lastKnown));
      }
      // isRefresh: the roles on screen ARE the last known roles — leave them untouched.
      setRolesLoading(false);
      return true;
    };

    instance
      .acquireTokenSilent({ ...apiTokenRequest, account })
      .then(
        (response) => {
          if (cancelled) return;
          fetchEffectiveRoles(accountKey, () => Promise.resolve(response.accessToken))
            .then((resolved) => {
              if (cancelled) return;
              resolvedAccountKey.current = accountKey;
              setRoles(resolved);
              // A 200 with no roles = the account holds no role here = no access.
              setEntitlement(entitlementFromRoles(resolved));
              setRolesLoading(false);
            })
            .catch(() => {
              if (cancelled) return;
              // #24685 — a refresh that fails keeps the last-known roles; the next trigger retries.
              if (keepLastKnownRoles()) return;
              // BR-S-EFR-005 — first load fails closed. 401, 403, 5xx, timeout, network failure or a
              // malformed body: roles stay empty and the state is transientError (the #19757
              // semantics). The token's roles claim is never used as a fallback.
              resolvedAccountKey.current = null;
              setRoles([]);
              setEntitlement('transientError');
              setRolesLoading(false);
            });
        },
        (error: unknown) => {
          if (cancelled) return;
          // #24685 — a refresh whose token renewal fails keeps the last-known roles. If only an
          // interactive sign-in can fix it, start the single-flight re-auth now (the operator comes
          // back to this page); a transient failure is simply retried on the next trigger.
          // AADSTS50105 is excluded: Entra refusing the account is an answer, not a failure, and it
          // is still a denial as in #19757.
          if (!isNotAssignedError(error) && keepLastKnownRoles()) {
            if (needsInteraction(error)) startReauth(instance, account, apiTokenRequest);
            return;
          }
          // First load: classified exactly as before (#19757). AADSTS50105 (not assigned) is a
          // denial; anything else — network, timeout, interaction required — is transient, and
          // getAccessToken handles the redirect if interaction is genuinely needed on the next API
          // call.
          resolvedAccountKey.current = null;
          setRoles([]);
          setEntitlement(entitlementFromError(error));
          setRolesLoading(false);
        },
      );
    return () => {
      cancelled = true;
    };
    // accountKey is derived from account; rolesGeneration is the invalidation trigger.
  }, [instance, account, accountKey, rolesGeneration]);

  const displayName = account?.name ?? account?.username ?? '';
  const email = account?.username ?? '';
  const entraId = account?.localAccountId ?? '';

  return {
    isAuthenticated,
    account,
    displayName,
    email,
    entraId,
    roles,
    rolesLoading,
    entitlement,
    login,
    logout,
    getAccessToken,
    handleSessionExpired,
  };
}
